import React, { Fragment } from "react";
import TableLayout from "../layouts/TableLayout";
import SliderLayout from "../layouts/SliderLayout";
import {
    friendlyNumber,
    estimateCostByEmailCount,
    findPlanByHandle,
    findOutboundIndexByHandle,
    findRateLimitedIndices,
    friendlyPrice,
    getOutboundSliderFromAllocation,
    getOutboundAllocationFromSlider,
    getAllPricesRange,
    estimateOverageCostByEmailCount,
    findOutboundByEmailCount,
    roundToLeft,
} from "../../utils/calculators";
import { usePlansContext } from "../../context/PlansContext";
import { useAppContext } from "../../context/AppContext";
import { OUTBOUND, EMAIL_RANGE_LIMIT } from "../../utils/constants";
import { updateOutbound } from "../../actions/appActions";
import styles from "./plan.module.less";

const sliderPrecision = 100;

export const customPricingOutbound = (
    <span>
        <b>Need to send more?</b>{" "}
        <a
            href="https://info.mailchannels.com/contact-sales"
            rel="noopener noreferrer"
            target="_blank"
        >
            Contact us{" "}
        </a>
        {` for
high volume pricing that starts at ${friendlyNumber(EMAIL_RANGE_LIMIT)} emails per month.`}
    </span>
);

const OutboundSlider = props => {
    const {
        state: { outbound },
        dispatch,
    } = useAppContext();

    const {
        state: { plans },
    } = usePlansContext();

    if (!(plans && outbound && outbound.planHandle)) return null; //don't render if either plans and outboundIndex are absent

    const outboundPlan = findPlanByHandle(outbound.planHandle, plans[OUTBOUND]);
    const allPrices = getAllPricesRange(plans[OUTBOUND]);
    const { allocation } = outbound;
    const sliderValue = getOutboundSliderFromAllocation(allocation, allPrices);
    const estimateCost = estimateCostByEmailCount(allocation, outboundPlan);

    const handleSliderChange = value => {
        //update context
        const newAllocation = roundToLeft(
            //round to left 2 digits for easy display
            getOutboundAllocationFromSlider(value / sliderPrecision, allPrices)
        );
        const bestOutbound = findOutboundByEmailCount(newAllocation, plans[OUTBOUND]);
        dispatch(
            updateOutbound({
                planHandle: bestOutbound.planHandle,
                allocation: newAllocation,
            })
        );
    };

    const handleRowClick = plan => {
        const { planHandle, includedEmails } = plan;
        dispatch(updateOutbound({ planHandle: planHandle, allocation: includedEmails }));
    };

    const columns = [
        {
            title: "Plan",
            dataIndex: "planName",
            key: "planName",
        },
        {
            title: "Included emails",
            dataIndex: "includedEmails",
            width: "10rem",
            align: "center",
            render: (text, plan) => {
                const includedEmails = plan.includedEmails;
                return friendlyNumber(includedEmails);
            },
        },
        {
            title: "Extra emails",
            dataIndex: "extraEmails",
            width: "10rem",
            align: "center",
            render: (text, plan) => {
                const includedEmails = plan.includedEmails;
                const extraEmails = allocation - includedEmails;
                return extraEmails > 0 ? friendlyNumber(extraEmails) : 0;
            },
        },
        {
            title: "Base price",
            dataIndex: "basePrice",
            key: "basePrice",
            width: "10rem",
            align: "center",
            render: text => {
                return friendlyPrice(text);
            },
        },
        {
            width: "1rem",
            align: "center",
            render: () => "+",
        },
        {
            title: "Overage cost estimate",
            dataIndex: "overageCostEstimate",
            key: "overageCostEstimate",
            width: "10rem",
            align: "center",
            render: (text, plan) => {
                const overageCost = estimateOverageCostByEmailCount(allocation, plan);
                return friendlyPrice(overageCost);
            },
        },
        {
            width: "1rem",
            align: "center",
            render: () => "=",
        },
        {
            title: "Monthly estimate",
            dataIndex: "monthlyEstimate",
            width: "10rem",
            align: "center",
            render: (text, record) => {
                const plan = record;
                const { basePrice } = plan;
                const overageCost = estimateOverageCostByEmailCount(allocation, plan);
                return friendlyPrice(basePrice + overageCost);
            },
        },
    ];

    return (
        <div style={{ margin: "4rem 0px 2rem" }} data-test-id="outbound-slider-handle">
            <SliderLayout
                title={"How many outbound emails do you send per month?"}
                leftLabel={
                    <Fragment>
                        <strong>{`${friendlyNumber(allocation)} emails/mo`}</strong>
                    </Fragment>
                }
                rightLabel={
                    <Fragment>
                        <strong>{`${friendlyPrice(estimateCost)}/mo`}</strong>
                        <div className={styles.estimated}>{" estimated"}</div>
                    </Fragment>
                }
                SliderMaxMessage={customPricingOutbound}
                step={1}
                value={sliderValue * sliderPrecision}
                max={sliderPrecision}
                onChange={value => handleSliderChange(value)}
            />
            <TableLayout
                highlightRowIndex={findOutboundIndexByHandle(outbound.planHandle, plans[OUTBOUND])}
                rateLimitedIndices={findRateLimitedIndices(outbound.allocation, plans[OUTBOUND])}
                onRowClick={index => handleRowClick(plans[OUTBOUND][index])}
                dataSource={plans[OUTBOUND]}
                columns={columns}
            />
        </div>
    );
};

export default OutboundSlider;
